import { Host, h } from "@stencil/core";
const ARIA_SORT_MAP = {
    undefined: "none",
    asc: "ascending",
    desc: "descending"
};
export class ChTabularGridRender {
    constructor() {
        this.columnHideable = true;
        this.columnResizable = true;
        this.columnSortable = true;
        this.model = undefined;
        this.theme = undefined;
    }
    #modelVersion = 0;
    modelChanged() {
        this.#modelVersion++;
    }
    #isRowsetItemSimpleModel = (rowset) => rowset && !("id" in rowset);
    #isRowsetItemGroupModel = (rowset) => rowset && "id" in rowset;
    #renderGrid = (columns, rowsets) => (h("ch-tabular-grid", { key: this.#modelVersion, class: "tabular-grid" }, this.#renderColumns(columns), this.#renderRowsets(rowsets)));
    #renderColumns = (columns) => (h("ch-tabular-grid-columnset", { parts: true, class: "tabular-grid-column-set" }, columns.map(this.#renderColumn)));
    #renderColumn = (column) => (h("ch-tabular-grid-column", { parts: column.parts ?? true, role: "columnheader", "aria-label": column.accessibleName, "aria-sort": ARIA_SORT_MAP[column.sortDirection], class: "tabular-grid-column", columnId: column.id, columnName: column.caption, columnNameHidden: column.captionHidden, columnTooltip: column.tooltip, order: column.order, size: column.size, resizable: column.resizable ?? this.columnResizable, sortable: column.sortable ?? this.columnSortable, sortDirection: column.sortDirection, hidden: column.hidden, hideable: column.hideable ?? this.columnHideable, freeze: column.freeze, settingable: false }));
    #renderRowsets = (rowsets) => {
        const normalizedModel = Array.isArray(rowsets) ? rowsets : [rowsets];
        return normalizedModel.map(rowset => {
            if (this.#isRowsetItemSimpleModel(rowset)) {
                return this.#renderRowsetSimple(rowset.rows, rowset.rowsets);
            }
            if (this.#isRowsetItemGroupModel(rowset)) {
                return this.#renderRowsetGroup(rowset);
            }
        });
    };
    #renderRowsetSimple = (rows, rowsets) => (h("ch-tabular-grid-rowset", { role: "rowgroup", class: "tabular-grid-rowset" }, rows.map(this.#renderRow), rowsets && rowsets.map(this.#renderRowsetGroup)));
    #renderRowsetGroup = (rowset) => (h("ch-tabular-grid-rowset", { rowsetId: rowset.id, role: "rowgroup", class: "tabular-grid-rowset" }, h("ch-tabular-grid-rowset-legend", { parts: rowset.parts ?? true, "aria-label": rowset.accessibleName, class: "tabular-grid-rowset-legend" }, rowset.caption), rowset.rows.map(this.#renderRow), rowset.rowsets && rowset.rowsets.map(this.#renderRowsetGroup)));
    #renderRow = (row) => (h("ch-tabular-grid-row", { rowid: row.id,
        /**
         * TODO: Replace the temporary workaround of using "ref={}" to assign the property instead of the attribute.
         * Use "parts={row.parts ?? true}" when <ch-tabular-grid-row> correctly declare the JSX property.
         */
        ref: (el) => {
            if (el) {
                el.parts = row.parts ?? true;
            }
        }, role: "row", class: "tabular-grid-row" }, row.cells.map(this.#renderCell), row.rows && this.#renderRowsetSimple(row.rows)));
    #renderCell = (cell, index) => {
        return (h("ch-tabular-grid-cell", { cellid: cell.id,
            /**
             * TODO: Replace the temporary workaround of using "ref={}" to assign the property instead of the attribute.
             * Use "parts={row.parts ?? true}" when <ch-tabular-grid-cell> correctly declare the JSX property.
             */
            ref: (el) => {
                if (el) {
                    el.parts = cell.parts ?? true;
                }
            }, role: "gridcell", "aria-colindex": ++index, class: "tabular-grid-cell" }, cell.text));
    };
    render() {
        return (h(Host, { key: 'd5bf2c5b7f0308588b694bf63d32fa389e09787f', role: "grid" }, this.theme && h("ch-theme", { key: 'cf270fca017f1afe615cf65c6172ab8835304b8c', model: this.theme }), this.model && this.#renderGrid(this.model.columns, this.model.rowsets)));
    }
    static get is() { return "ch-tabular-grid-render"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["tabular-grid-render.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["tabular-grid-render.css"]
        };
    }
    static get properties() {
        return {
            "columnHideable": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Determines if the columns can be hidden by the user"
                },
                "attribute": "column-hideable",
                "reflect": false,
                "defaultValue": "true"
            },
            "columnResizable": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Determines if the columns can be resized by the user."
                },
                "attribute": "column-resizable",
                "reflect": false,
                "defaultValue": "true"
            },
            "columnSortable": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Determines if the columns can be sorted by the user."
                },
                "attribute": "column-sortable",
                "reflect": false,
                "defaultValue": "true"
            },
            "model": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "TabularGridModel",
                    "resolved": "{ columns: TabularGridColumnsModel; rowsets: TabularGridRowsetsModel; }",
                    "references": {
                        "TabularGridModel": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/tabular-grid-render/types.ts::TabularGridModel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the content of the tabular grid control."
                }
            },
            "theme": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "TODO: Remove this property"
                },
                "attribute": "theme",
                "reflect": false
            }
        };
    }
    static get watchers() {
        return [{
                "propName": "model",
                "methodName": "modelChanged"
            }];
    }
}
